import { Component, inject, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { NotificationService } from '../../services/notification.service';
import { NotificationItem } from '../../models/notification.model';

@Component({
  selector: 'black-notification-bell',
  templateUrl: './notification-bell.component.html',
  standalone: false,
})
export class NotificationBellComponent implements OnInit {
  private notificationService = inject(NotificationService);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);

  public unreadCount$ = this.notificationService.unreadCount$;
  public notifications$ = this.notificationService.notifications$;
  public isLoading$ = this.notificationService.isLoading$;

  public isOpen = false;
  public showPushPrompt = false;
  public isRegisteringPush = false;

  ngOnInit(): void {
    // Initial fetch of unread counter
    this.notificationService.fetchUnreadCount().subscribe();
    this.checkPushPrompt();
  }

  private checkPushPrompt(): void {
    this.showPushPrompt =
      isPlatformBrowser(this.platformId) &&
      'Notification' in window &&
      Notification.permission === 'default' &&
      !localStorage.getItem('push_prompt_dismissed');
  }

  async enablePushNotifications(): Promise<void> {
    this.isRegisteringPush = true;
    try {
      await this.notificationService.requestPushSubscription();
    } finally {
      this.isRegisteringPush = false;
      this.showPushPrompt = false;
    }
  }

  dismissPushPrompt(): void {
    this.showPushPrompt = false;
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('push_prompt_dismissed', 'true');
    }
  }

  toggleDropdown(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.notificationService.fetchNotifications(1, 10).subscribe();
    }
  }

  closeDropdown(): void {
    this.isOpen = false;
  }

  onNotificationClick(item: NotificationItem): void {
    if (!item.isRead) {
      this.notificationService.markAsRead(item._id).subscribe();
    }
    this.closeDropdown();

    if (item.actionUrl) {
      this.router.navigateByUrl(item.actionUrl);
    }
  }

  markAllAsRead(event: Event): void {
    event.stopPropagation();
    this.notificationService.markAllAsRead().subscribe();
  }

  deleteNotification(item: NotificationItem, event: Event): void {
    event.stopPropagation();
    this.notificationService.deleteNotification(item._id).subscribe();
  }

  getIconClass(item: NotificationItem): string {
    if (item.icon) {
      return item.icon;
    }

    if (item.type?.includes('invoice') || item.type?.includes('payment')) {
      return 'fa-solid fa-file-invoice-dollar text-primary';
    }
    if (item.type?.includes('announcement')) {
      return 'fa-solid fa-bullhorn text-primary';
    }
    if (item.type?.includes('recording')) {
      return 'fa-solid fa-play text-secondary';
    }
    if (item.type?.includes('class') || item.type?.includes('meeting')) {
      return 'fa-solid fa-video text-secondary';
    }
    if (item.type?.includes('welcome') || item.type?.includes('invitation')) {
      return 'fa-solid fa-user-plus text-accent';
    }

    return 'fa-solid fa-bell text-info';
  }

  formatTime(dateInput: string | Date | undefined): string {
    if (!dateInput) return '';
    const date = new Date(dateInput);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHours = Math.floor(diffMin / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSec < 60) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  }
}
